
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { DeleteOutline, Duplicate } from '../../utils/editorReactIcons';

import FitTextToContentAction from '../common/FitTextToContentAction';
import FloatingGroupLockActions from '../common/GroupLockControls/FloatingGroupLockActions';
import LayerOrderControls from '../common/LayerOrderControls';
import {
  FLOATING_TOOLBAR_ICON_SIZE,
  StyledFloatingToolbarButton,
} from '../common/FloatingToolbar.styled';
import { useStore } from '../../hooks';
import { DUPLICATE_ANNOTATIONS, REMOVE_ANNOTATIONS } from '../../actions';
import { filterUnlockedAnnotationIds } from '../../utils/annotationLockUtils';
import {
  NODES_TRANSFORMER_ID,
  TOOLS_IDS,
  WATERMARK_ANNOTATION_ID,
} from '../../utils/constants';
import measureSelectionToolbarPosition from '../../utils/measureSelectionToolbarPosition';
import { StyledNodeControls } from './NodeControls.styled';

const NodeControls = () => {
  const { selectionsIds = [], annotations = {}, designLayer, dispatch, t } =
    useStore();
  const mutableSelectionIds = useMemo(
    () => filterUnlockedAnnotationIds(annotations, selectionsIds),
    [annotations, selectionsIds],
  );
  const canMutateSelection = mutableSelectionIds.length > 0;
  const showFitTextAction = selectionsIds.some(
    (id) =>
      annotations[id]?.name === TOOLS_IDS.TEXT && !annotations[id]?.locked,
  );
  const [position, setPosition] = useState(null);
  const selectionsLength = selectionsIds.length;
  const selectionKey = selectionsIds.join(',');

  const measureToolbarPosition = useCallback(
    () => measureSelectionToolbarPosition(designLayer, selectionsIds, annotations),
    [annotations, designLayer, selectionKey, selectionsIds],
  );

  useLayoutEffect(() => {
    if (selectionsLength === 0) {
      setPosition(null);
      return undefined;
    }

    let cancelled = false;

    const syncPosition = () => {
      if (cancelled) {
        return null;
      }
      const next = measureToolbarPosition();
      setPosition(next);
      return next;
    };

    syncPosition();

    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      syncPosition();
      if (!cancelled) {
        raf2 = requestAnimationFrame(syncPosition);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2 !== null) {
        cancelAnimationFrame(raf2);
      }
    };
  }, [measureToolbarPosition, selectionKey, selectionsLength]);

  useEffect(() => {
    if (selectionsLength === 0 || !designLayer) {
      return undefined;
    }

    const layer = designLayer;
    const stage = layer.getStage?.();
    const transformer = stage?.findOne(`#${NODES_TRANSFORMER_ID}`);

    const onTransformChange = () => {
      setPosition(measureToolbarPosition());
    };

    layer.on('dragmove transform', onTransformChange);
    transformer?.on('transform', onTransformChange);

    return () => {
      layer.off('dragmove transform', onTransformChange);
      transformer?.off('transform', onTransformChange);
    };
  }, [designLayer, measureToolbarPosition, selectionKey, selectionsLength]);

  const duplicateSelectedNodes = () => {
    if (!canMutateSelection) {
      return;
    }
    dispatch({
      type: DUPLICATE_ANNOTATIONS,
      payload: {
        annotationsIds: mutableSelectionIds,
      },
    });
  };

  const removeSelectedNodes = () => {
    if (!canMutateSelection) {
      return;
    }
    dispatch({
      type: REMOVE_ANNOTATIONS,
      payload: {
        annotationsIds: mutableSelectionIds,
      },
    });
  };

  if (selectionsLength === 0 || !position) {
    return null;
  }

  const duplicateTitle = t('duplicateSelectionTitle') || 'Duplicate (Ctrl+D)';
  const deleteTitle = t('deleteSelectionTitle') || 'Delete (Backspace)';

  return (
    <StyledNodeControls
      className="respark_annotation-controls-overlay respark_floating-selection-toolbar"
      left={position.left}
      top={position.top}
    >
      {selectionsIds[0] !== WATERMARK_ANNOTATION_ID && (
        <StyledFloatingToolbarButton
          type="button"
          className="respark_floating-toolbar-btn respark_floating-toolbar-btn--duplicate"
          title={duplicateTitle}
          aria-label={duplicateTitle}
          onClick={duplicateSelectedNodes}
          disabled={!canMutateSelection}
        >
          <Duplicate size={FLOATING_TOOLBAR_ICON_SIZE} />
        </StyledFloatingToolbarButton>
      )}
      {showFitTextAction && (
        <FitTextToContentAction
          variant="floating"
          annotationIds={selectionsIds}
          t={t}
        />
      )}
      <FloatingGroupLockActions />
      <LayerOrderControls variant="compact" />
      <StyledFloatingToolbarButton
        type="button"
        className="respark_floating-toolbar-btn respark_floating-toolbar-btn--delete"
        title={deleteTitle}
        aria-label={deleteTitle}
        onClick={removeSelectedNodes}
        disabled={!canMutateSelection}
      >
        <DeleteOutline size={FLOATING_TOOLBAR_ICON_SIZE} />
      </StyledFloatingToolbarButton>
    </StyledNodeControls>
  );
};

export default NodeControls;
